import {useMutation, useQuery, useQueryClient} from "react-query";
import { apiCall } from "Apis/BaseApi";
import {IQueryParams} from "./Models/IQueryParams";
import {SettlementResultDto, SettlementSalesDto} from "./Models/SettlementSalesDto";
import {ZettleSalesDto} from "./Models/ZettleSalesDto";
import {IPaginatedList} from "./Models/IPaginatedList";
import {ProjectSettlementDto} from "./Models/ProjectSettlementDto";
import {IProjectDto} from "./Models/IProjectDto";
import {useUserContext} from "../Providers/UserContextProvider";
import {canAccess} from "../Utils/CanAccess";
import {saveAs} from "file-saver";

export const SETTLEMENT_PROJECTS_CACHE_KEY = 'SETTLEMENT_PROJECTS_CACHE_KEY '
export const useSettlementProjects = (queryable: Partial<IQueryParams> | null = null) => {
    return useQuery<IProjectDto[]>([SETTLEMENT_PROJECTS_CACHE_KEY], async () => {
        return await apiCall<IProjectDto[]>({
            url: `/projectSettlements/projects`,
            method: 'GET',
            queryParams: queryable
        })
    });
}

export const PROJECT_SETTLEMENTS_CACHE_KEY = 'PROJECT_SETTLEMENTS_CACHE_KEY '
export const useProjectSettlements = (queryable: Partial<IQueryParams> | null = null) => {
    return useQuery<IPaginatedList<ProjectSettlementDto>>([PROJECT_SETTLEMENTS_CACHE_KEY, queryable], async () => {
        return await apiCall<IPaginatedList<ProjectSettlementDto>>({
            url: `/projectSettlements`,
            method: 'GET',
            queryParams: queryable
        })
    });
}

export const useOpenProjectSettlements = (queryable: Partial<IQueryParams> | null = null) => {
    return useQuery<ProjectSettlementDto[]>([PROJECT_SETTLEMENTS_CACHE_KEY, 'OPEN', queryable], async () => {
        return await apiCall<ProjectSettlementDto[]>({
            url: `/projectSettlements/open`,
            method: 'GET',
            queryParams: queryable
        })
    });
}

export const PROJECT_SETTLEMENT_SALES_CACHE_KEY = 'PROJECT_SETTLEMENT_SALES_CACHE_KEY '
export const useSettlementSales = (queryable: Partial<IQueryParams> | null = null) => {
    const { user } = useUserContext();
    
    return useQuery<SettlementSalesDto>([PROJECT_SETTLEMENT_SALES_CACHE_KEY, queryable], async () => {
        return await apiCall<SettlementSalesDto>({
            url: `/projectSettlements/sales`,
            method: 'GET',
            queryParams: queryable
        })
    }, {
        enabled: ((queryable && queryable['from'] !== undefined && queryable['to'] !== undefined) && (canAccess(user.permissions, 'Admin') || !!queryable['projectId'])) ?? false
    });
}

export const ZETTLE_SALES_CACHE_KEY = 'ZETTLE_SALES_CACHE_KEY '
export const useZettleSales = (queryable: Partial<IQueryParams> | null = null) => {
    const { user } = useUserContext();

    return useQuery<ZettleSalesDto>([ZETTLE_SALES_CACHE_KEY, queryable], async () => {
        return await apiCall<ZettleSalesDto>({
            url: `/projectSettlements/zettleSales`,
            method: 'GET',
            queryParams: queryable
        })
    }, {
        enabled: ((queryable && queryable['from'] !== undefined && queryable['to'] !== undefined) && (canAccess(user.permissions, 'Admin') || !!queryable['projectId'])) ?? false
    });
}

export const PROJECT_SETTLEMENT_RESULT_CACHE_KEY = 'PROJECT_SETTLEMENT_RESULT_CACHE_KEY '
export const useSettlementResult = (queryable: Partial<IQueryParams> | null = null, refetchInterval: number | undefined = undefined) => {
    return useQuery<SettlementResultDto>([PROJECT_SETTLEMENT_RESULT_CACHE_KEY, queryable], async () => {
        return await apiCall<SettlementResultDto>({
            url: `/projectSettlements/calculateResult`,
            method: 'GET',
            queryParams: queryable
        })
    }, {
        enabled: (queryable && queryable['from'] !== undefined && queryable['to'] !== undefined && queryable['projectId'] !== undefined) ?? false,
        refetchInterval
    });
}

export const useSettlePeriod = () => {
    return useMutation(async (queryable: Partial<IQueryParams> | null = null) => {
        return await apiCall<ProjectSettlementDto>({
            url: `/projectSettlements/settle`,
            method: 'POST',
            queryParams: queryable
        });
    })
}


export const useReopenSettlement = () => {
    
    const queryClient = useQueryClient();
    
    return useMutation(async (settlementId: number) => {
        return await apiCall<ProjectSettlementDto>({
            url: `/projectSettlements/${settlementId}/reopen`,
            method: 'PUT',
        });
    },  {
        onSuccess: async () => {
            await queryClient.invalidateQueries(SETTLEMENT_PROJECTS_CACHE_KEY)
            await queryClient.invalidateQueries(PROJECT_SETTLEMENTS_CACHE_KEY)
        }
    })
}

export const useDownloadSettlementPdf = () => {
    return useMutation(async (id: number) => {
        return await apiCall<any>({
            responseType: 'blob',
            url: `/projectSettlements/download/${id}`,
            method: 'GET',
        });
    })
}

export const getSettlementCSV = async (queryParams: Partial<IQueryParams> | null = null, fileName: string): Promise<any> => {
    const responseBlob =  await apiCall<Blob>({
        url: `/projectSettlements/downloadSettlementCsv`,
        method: 'GET',
        queryParams: queryParams,
        responseType: 'blob',
    })

    const blob = new Blob([responseBlob], {type: 'application/csv;charset=UTF-8' });

    saveAs(blob, fileName);
}